import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { Divider } from "@mui/material";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { useChartPalette } from "../utils/chartUtils";
import { getChartTooltipStyle, getCartesianGridProps } from "../utils/chartStyles";
import type { SpecificScatterPoint } from "@/types";
import { currencyCompactNotation, formatUSD } from "@/utils/formatters/currencyFormatter";
import { useAppTheme } from "@/hooks/useAppTheme";

interface SpecDeductibleChartProps {
  data: SpecificScatterPoint[];
}

/**
 * Scatter plot of Spec Deductible vs Group Size per policy.
 * Each dot is colored by carrier.
 *
 * @param data - Pre-aggregated scatter points from the analytics endpoint.
 */
export const SpecDeductibleChart = ({ data: rawData }: SpecDeductibleChartProps) => {
  const { theme } = useAppTheme();
  const chartPalette = useChartPalette();

  if (rawData.length === 0) {
    return <EmptyChartState />;
  }

  const data = rawData
    .filter((d) => d.group_size > 0)
    .map((d) => ({
      x: d.specific_deductible,
      y: d.group_size,
      carrier: d.carrier_name,
      policyholder: d.client_name,
    }));

  if (data.length === 0) {
    return <EmptyChartState />;
  }

  // Get unique carriers for consistent color assignment
  const carriers = [...new Set(data.map((d) => d.carrier))];

  const getColor = (carrier: string) => {
    const index = carriers.indexOf(carrier);
    return chartPalette[index % chartPalette.length];
  };

  return (
    <ResponsiveContainer width="100%" height={280}>
      <ScatterChart margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
        <CartesianGrid {...getCartesianGridProps(theme)} />
        <XAxis
          type="number"
          dataKey="x"
          name="Spec Deductible"
          tickFormatter={(value) => formatUSD(value, currencyCompactNotation)}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 10 }}
        />
        <YAxis type="number" dataKey="y" name="Group Size" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
        <Tooltip
          content={({ payload }) => {
            if (!payload || !payload.length) return null;
            const item = payload[0].payload;
            return (
              <div
                style={{
                  ...getChartTooltipStyle(theme),
                  padding: "8px",
                }}
              >
                <strong>{item.policyholder}</strong>
                <div>{item.carrier}</div>
                <Divider sx={{ my: 1 }} />
                <div>Spec Deductible: {formatUSD(item.x, currencyCompactNotation)}</div>
                <div>Group Size: {item.y.toLocaleString()}</div>
              </div>
            );
          }}
        />
        <Scatter data={data}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getColor(entry.carrier)} />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};
